<template>
    <div class="modal-content" ref="container">
        <div class="modal-header">
            <h2 class="modal-title">Confirm Return</h2>
            <button type="button" class="close icon icon-close" @click="close" autofocus :disabled="submitting" aria-label="Close" />
        </div>
        <div class="modal-body">
            <div v-if="submitting">
                <LoadingWheel :width="100" />
            </div>
            <template v-else>
                <div v-if="messages.length > 0" class="alert alert-danger">
                    <span class="sr-only">Validation:</span>
                    <ul>
                        <li v-for="message in messages">{{ message }}</li>
                    </ul>
                </div>
                <p describedby-text>Are you sure you want to return this submission?</p>
                <div class="form-group" v-if="isWorkflow">
                    <label for="ReturnConfirmSegment">Returning to stage:</label>
                    <select id="ReturnConfirmSegment" class="form-control" v-model="returningSegment">
                        <option v-for="segment in possibleStagesToReturn" :value="segment.id">{{ segment.definition.settings.reviewerStatus || segment.getLabel() }}</option>
                    </select>
                </div>
                <div class="form-group" v-if="!isWorkflow">
                    <p describedby-text>This will prompt the submitter to correct and resubmit this submission.</p>
                </div>
                <div class="form-group">
                    <label for="ReturnComment">Returning with comment:</label>
                    <textarea row="3" v-model="comment" id="ReturnComment" class="form-control" placeholder="A comment is required" />
                </div>
            </template>
        </div>
        <div class="modal-footer">
            <button id="returnBtn" class="btn btn-primary" @click="submit()" :disabled="submitting">Return</button>
            <button id="cancelBtn" class="btn btn-outline-secondary" @click="close" :disabled="submitting">Cancel</button>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, SetupContext } from "vue";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import { usePageStore } from "../pageState/store";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import { AddCommentResponse } from "@cs/AddCommentResponse";
type ReturnDraftResponse = {
    success: boolean;
    messages: string[];
};
type Props = {
    returnFunction: (returningSegment: string | null, comment: string | null) => Promise<ReturnDraftResponse & AddCommentResponse>;
} & DialogProps;

export default defineComponent({
    components: {
        LoadingWheel,
    },
    props: ["returnFunction", "triggerElement"],
    setup(props: Props, { emit }: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef, props);

        const returningSegment = ref(null as null | string);
        const submitting = ref(false);
        const comment = ref(null as string | null);
        const messages = ref([] as string[]);

        const pageStore = usePageStore();
        const completedSegments = computed(() => pageStore.completedSegments);
        const possibleStagesToReturn = computed(() => pageStore.possibleStagesToReturn);
        const isWorkflow = computed(() => pageStore.isWorkflow);

        const submit = async () => {
            if (!isWorkflow.value && (comment.value == null || comment.value.trim().length == 0)) {
                messages.value = ["Comment is required"];
                return;
            }
            submitting.value = true;
            const result = await props.returnFunction(returningSegment.value, comment.value);
            submitting.value = false;
            if (result.success) {
                emit("close");
            } else {
                messages.value = result.messages;
            }
        };

        return {
            container: containerRef,
            returningSegment,
            messages,
            comment,
            isWorkflow,
            completedSegments,
            possibleStagesToReturn,
            submit,
            submitting,

            close: () => emit("close"),
        };
    },
});
</script>
